import React, { useState, useEffect } from "react";
import { Button, Form, Modal, Pagination, Spinner } from "react-bootstrap";
import SchoolSidebar from "./SchoolSidebar";
import axios from "axios";

const SchoolUniformCompleted = () => {
  const [data, setData] = useState([]);

  const [mode, setMode] = useState("");
  const [enrollmentNumbers, setEnrollmentNumbers] = useState([]);
  const [grades, setGrades] = useState([]);
  const [gorb, setGorB] = useState([]);

  // Filters
  const [filterEnrollment, setFilterEnrollment] = useState("");
  const [filterGroup, setFilterGroup] = useState("");
  const [filterGrade, setFilterGrade] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Modal for products
  const [showModal, setShowModal] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleViewProducts = (products) => {
    setSelectedProducts(products);
    setShowModal(true);
  };

  const renderPages = () => {
    const pages = [];

    if (totalPages <= 5) {
      // small case → show all
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (page > 3) pages.push("...");

      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (page < totalPages - 2) pages.push("...");

      pages.push(totalPages);
    }

    return pages;
  };

  useEffect(() => {
    const fetchSchool = async () => {
      try {
        const schoolId = localStorage.getItem("schoolID");
        const resp = await axios.get(
          `/api/schooluniform/get-students-by-school/${schoolId}`
        );
        const students = resp.data.data;
        const gOrb = resp.data.gOrb;

        // Extract dropdown values
        const enrollmentList = students.map((s) => s.enrollment_number);

        // Extract grade dropdown (store only grade._id)
        const gradeList = students.map((s) => ({
          id: s.grade?._id,
          name: s.grade?.name,
        }));

        const gOrbList = Array.from(
          new Map(
            gOrb.map((s) => [s._id, { _id: s._id, name: s.name }])
          ).values()
        );

        // Optional: remove duplicates
        const uniqueGrades = Array.from(
          new Map(gradeList.map((g) => [g.id, g])).values()
        );

        // Update states
        setEnrollmentNumbers(enrollmentList);
        setGorB(gOrbList);
        setMode(resp.data.option);
        setGrades(uniqueGrades);
      } catch (err) {
        console.error(err);
      }
    };

    fetchSchool();
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const schoolId = localStorage.getItem("schoolID");
        const response = await axios.get(
          "/api/schooluniform/completed-orders",
          {
            params: {
              schoolId,
              page,
              limit,
              grade: filterGrade,
              enrollment: filterEnrollment,
              group: filterGroup,
            },
          }
        );

        if (response.data.status === "ok") {
          setData(response.data.data);
          setTotalPages(response.data.totalPages);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [page, limit, filterGrade, filterEnrollment, filterGroup]);

  return (
    <div style={{ display: "flex", height: "100%" }}>
      <SchoolSidebar />
      <div className="container mt-5 p-3 shadow-lg rounded-4 bg-light border mb-4 w-full">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="fw-bold text-primary m-0">Completed Orders List</h3>
        </div>

        {/* ---------------- FILTERS ---------------- */}
        <div className="row mb-3">
          <div className="col-md-4">
            <Form.Group>
              <Form.Label>Filter by Enrollment No</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter enrollment number"
                value={filterEnrollment}
                onChange={(e) => setFilterEnrollment(e.target.value)}
                list="enrollment-options"
              />
              <datalist id="enrollment-options">
                {enrollmentNumbers
                  .filter((e) =>
                    e.toLowerCase().includes(filterEnrollment.toLowerCase())
                  )
                  .map((e, index) => (
                    <option key={index} value={e} />
                  ))}
              </datalist>
            </Form.Group>
          </div>

          <div className="col-md-4">
            <Form.Group>
              <Form.Label>Filter by Group / Bundle</Form.Label>
              <Form.Select
                value={filterGroup} // store _id
                onChange={(e) => setFilterGroup(e.target.value)}
              >
                <option value="">
                  Select {mode === "group" ? "Group" : "Bundle"}
                </option>
                {gorb.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>

          <div className="col-md-4">
            <Form.Group>
              <Form.Label>Filter by Grade</Form.Label>
              <Form.Select
                value={filterGrade}
                onChange={(e) => setFilterGrade(e.target.value)}
              >
                {/* Placeholder / default option */}
                <option value="">All</option>

                {/* Dynamic options from grades array */}
                {grades.map((g) => (
                  <option key={g._id || g.id} value={g._id || g.id}>
                    {g.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
        </div>

        {/* ---------------- TABLE ---------------- */}
        <div className="mt-4 p-3 shadow rounded bg-white">
          <table className="table table-hover table-striped border rounded">
            <thead>
              <tr>
                <th>Enrollment No</th>
                <th>Student Name</th>
                <th>Group/Bundle</th>
                <th>Grade</th>
                <th>Gender</th>
                <th style={{ width: "220px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="6" className="text-center py-3">
                    <Spinner animation="border" />
                  </td>
                </tr>
              )}
              {!loading && data.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-3">
                    No records found
                  </td>
                </tr>
              )}

              {data.map((item) => (
                <tr key={item.id}>
                  <td>{item.enrollment_no}</td>
                  <td>{item.name}</td>
                  <td>{item.group_bundle}</td>
                  <td>{item.grade}</td>
                  <td>{item.gender}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        variant="info"
                        size="sm"
                        onClick={() => handleViewProducts(item.products)}
                      >
                        View Products
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination className="mt-3">
              {renderPages().map((p, idx) =>
                p === "..." ? (
                  <Pagination.Ellipsis key={`e-${idx}`} disabled />
                ) : (
                  <Pagination.Item
                    key={p}
                    active={page === p}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </Pagination.Item>
                )
              )}
            </Pagination>
          )}
        </div>
      </div>

      {/* ---------------- PRODUCTS MODAL ---------------- */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header className="bg-primary text-white" closeButton>
          <Modal.Title>Product List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <table className="table table-hover table-striped border rounded">
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Size</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {selectedProducts.map((p, idx) => (
                <tr key={idx}>
                  <td>{p.name}</td>
                  <td>{p.size}</td>
                  <td>{p.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SchoolUniformCompleted;
